<template>
  <ConfirmDialog
    :open="open"
    max-height="530"
    class-name="password-change-dialog"
    :disabled-confirm-action="loading"
    @cancel="onCancel"
    @confirm="onConfirm"
  >
    <template #title>Edit password</template>
    <template #detail>Enter your current password to be able to create a new one.</template>
    <template #body>
      <div class="form__content">
        <PasswordChange
          ref="passwordChangeForm"
          v-model:old-password="oldPassword"
          v-model:password="password"
          v-model:password-confirmation="passwordConfirmation"
          class="settings_pane__password-change"
          require-old-password
        />
      </div>
    </template>
    <template #confirm-text>Save new password</template>
  </ConfirmDialog>
</template>

<script lang="ts">
export default {
  name: 'PasswordChangeModal',
}
</script>

<script setup lang="ts">
import { ref } from 'vue'
import { useToast } from '@/uiKit/Toast/useToast'
import ConfirmDialog from '@/uiKit/Dialog/ConfirmDialog.vue'
import PasswordChange from '@/modules/Auth/PasswordChange.vue'
import { updateProfile } from '@/backend/darwin/updateProfile'

defineProps<{
  open: boolean
}>()

const toast = useToast()

const loading = ref<boolean>(false)

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'confirmed'): void
}>()

const onCancel = (): void => {
  emit('close')
}

const oldPassword = ref<string>('')
const password = ref<string>('')
const passwordConfirmation = ref<string>('')

const passwordChangeForm = ref<InstanceType<typeof PasswordChange>>()

const onConfirm = async (): Promise<void> => {
  loading.value = true

  const response = await updateProfile({
    old_password: oldPassword.value,
    password: password.value,
    password_confirmation: passwordConfirmation.value,
  })

  loading.value = false

  if (!response.ok && 'isValidationError' in response.error) {
    passwordChangeForm.value?.setErrors({
      oldPassword: response.error.old_password as string | undefined,
      password: response.error.password as string | undefined,
      passwordConfirmation: response.error.password_confirmation as string | undefined,
    })

    return
  }

  toast.success({ meta: { title: 'Password successfully updated' } })

  emit('confirmed')
}
</script>

<style lang="scss" scoped>
.form__content {
  padding: 16px;
}
</style>

<!-- eslint-disable-next-line vue-scoped-css/enforce-style-type -->
<style lang="scss">
/*  This `DarwinDialog` modal is normally called from `<modal>` and it needs to be on top of it.
    There is no current support to control z-index, so the below line is a workaround.
    `.password-change-dialog` is added to the dialog so we make sure the leaked css isn't
    changing the `z-index` of other dialogs.
*/
.darwin-dialog.password-change-dialog {
  z-index: var(--z-index-modal-level-2-overlay) !important;
}
</style>
